import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../api/client';

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';
const BG = '#f8f9fb';
const ACCENT = '#5352ed';

const ATTENDEE_COLOURS = ['#5352ed', '#e05252', '#27ae60', '#f39c12', '#8e44ad', '#16a085', '#c0392b', '#2980b9'];

function getColour(index) {
  return ATTENDEE_COLOURS[index % ATTENDEE_COLOURS.length];
}

// Highlight attendee names in a transcript paragraph
function AnnotatedText({ text, attendees }) {
  if (!attendees.length) return <span>{text}</span>;
  const namePattern = attendees.map(a => a.name.split(' ')[0]).filter(n => n.length > 2).join('|');
  if (!namePattern) return <span>{text}</span>;
  const regex = new RegExp(`\\b(${namePattern})\\b`, 'gi');
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) => {
        const idx = attendees.findIndex(a => a.name.toLowerCase().startsWith(part.toLowerCase()));
        if (idx >= 0) {
          return <mark key={i} style={{ background: getColour(idx) + '30', color: getColour(idx), borderRadius: 3, padding: '0 2px', fontWeight: 600 }}>{part}</mark>;
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

// ── CRM update section inside post-meeting card ──────────────────────────────
function CRMUpdateSection({ crmSuggestions, meetingTitle }) {
  const [checked, setChecked] = useState(() => new Set(crmSuggestions.map((_, i) => i)));
  const [updating, setUpdating] = useState(false);
  const [done, setDone] = useState(false);

  function toggle(i) {
    setChecked(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  }

  async function handleUpdate() {
    setUpdating(true);
    for (const [i, s] of crmSuggestions.entries()) {
      if (!checked.has(i)) continue;
      try {
        await api(`/api/contacts/${s.contactId}`, {
          method: 'PUT',
          body: JSON.stringify({
            lastContactedAt: new Date().toISOString(),
            notes: s.existingNotes ? `${s.existingNotes}\n${s.suggestNote}` : s.suggestNote,
          }),
        });
      } catch (_) {}
    }
    setUpdating(false);
    setDone(true);
  }

  if (done) {
    return (
      <div style={{ background: '#f6fff6', border: '1px solid #b8e6b8', borderRadius: 10, padding: '10px 14px' }}>
        <div style={{ fontSize: 12, color: '#27ae60', fontWeight: 700 }}>✓ CRM updated</div>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff9f0', border: '1px solid #ffd7a0', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Update CRM
      </div>
      {crmSuggestions.map((s, i) => (
        <label key={s.contactId} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(i)} style={{ marginTop: 2, accentColor: '#92400e', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#222' }}>{s.contactName}</div>
            <div style={{ fontSize: 11, color: '#666', lineHeight: 1.4 }}>
              Set last contacted to today · Add note: "{s.suggestNote}"
            </div>
          </div>
        </label>
      ))}
      <button
        onClick={handleUpdate}
        disabled={updating || checked.size === 0}
        style={{ background: checked.size > 0 ? '#92400e' : '#ccc', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}
      >
        {updating ? 'Updating…' : `Update ${checked.size} contact${checked.size === 1 ? '' : 's'}`}
      </button>
    </div>
  );
}

// ── Post-meeting review card ─────────────────────────────────────────────────
function PostMeetingCard({ postMeeting, attendees, onCreateTasks, onSendSummary, onDismiss }) {
  const items = postMeeting.actionItems || [];
  const [checked, setChecked] = useState(() => new Set(items.map((_, i) => i)));
  const [editedItems, setEditedItems] = useState(items.map(x => ({ ...x })));
  const [creating, setCreating] = useState(false);
  const allChecked = checked.size === items.length;

  function toggleAll() { setChecked(allChecked ? new Set() : new Set(items.map((_, i) => i))); }
  function toggle(i) { setChecked(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; }); }
  function updateField(i, field, val) {
    setEditedItems(prev => prev.map((item, idx) => idx === i ? { ...item, [field]: val } : item));
  }

  async function handleCreate() {
    setCreating(true);
    await onCreateTasks(editedItems.filter((_, i) => checked.has(i)));
  }

  const crmSuggestions = Array.isArray(postMeeting.crmSuggestions) ? postMeeting.crmSuggestions : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Summary */}
      <div style={{ background: '#f0f4ff', border: `1px solid #c5d5f5`, borderRadius: 10, padding: '12px 14px' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: ACCENT, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Meeting Summary</div>
        <div style={{ fontSize: 13, color: '#333', lineHeight: 1.55 }}>{postMeeting.summary}</div>
      </div>

      {/* Decisions */}
      {Array.isArray(postMeeting.decisions) && postMeeting.decisions.length > 0 && (
        <div style={{ background: '#f6fff6', border: `1px solid #b8e6b8`, borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#27ae60', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Key Decisions</div>
          {postMeeting.decisions.map((d, i) => (
            <div key={i} style={{ fontSize: 13, color: '#333', marginBottom: 3, display: 'flex', gap: 6 }}>
              <span style={{ color: '#27ae60', fontWeight: 700, flexShrink: 0 }}>✓</span>
              <span>{d}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action items */}
      {items.length > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, textTransform: 'uppercase', letterSpacing: 0.5 }}>Action Items</div>
            <button onClick={toggleAll} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: ACCENT, fontWeight: 600 }}>
              {allChecked ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {editedItems.map((item, i) => (
              <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', borderRadius: 8, border: `1px solid ${checked.has(i) ? ACCENT : BORDER}`, background: checked.has(i) ? '#f4f3ff' : '#fafafa', cursor: 'pointer' }}>
                <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(i)} style={{ marginTop: 3, accentColor: ACCENT, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <input
                    value={item.task}
                    onChange={e => updateField(i, 'task', e.target.value)}
                    style={{ fontSize: 12, fontWeight: 600, color: '#222', border: 'none', background: 'transparent', outline: 'none', width: '100%', padding: 0, fontFamily: 'inherit' }}
                  />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select
                      value={item.assignee || ''}
                      onChange={e => updateField(i, 'assignee', e.target.value)}
                      style={{ fontSize: 11, border: `1px solid ${BORDER}`, borderRadius: 4, padding: '2px 4px', fontFamily: 'inherit', background: '#fff' }}
                    >
                      <option value="">Unassigned</option>
                      {attendees.map((a, ai) => (
                        <option key={ai} value={a.name}>{a.name}</option>
                      ))}
                    </select>
                    <input
                      type="date"
                      value={item.dueDate || ''}
                      onChange={e => updateField(i, 'dueDate', e.target.value)}
                      style={{ fontSize: 11, border: `1px solid ${BORDER}`, borderRadius: 4, padding: '2px 4px', fontFamily: 'inherit' }}
                    />
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* CRM update suggestions */}
      {crmSuggestions.length > 0 && (
        <CRMUpdateSection crmSuggestions={crmSuggestions} />
      )}

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {items.length > 0 && (
          <button
            onClick={handleCreate}
            disabled={creating || checked.size === 0}
            style={{ background: checked.size > 0 ? NAVY : '#ccc', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: creating ? 0.7 : 1 }}
          >
            {creating ? 'Creating…' : `Create ${checked.size} task${checked.size === 1 ? '' : 's'}`}
          </button>
        )}
        <button
          onClick={onSendSummary}
          style={{ background: '#f0f4ff', border: `1px solid #c5d5f5`, borderRadius: 7, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: ACCENT }}
        >
          Send to attendees
        </button>
        <button
          onClick={onDismiss}
          style={{ background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 7, padding: '8px 14px', fontSize: 12, cursor: 'pointer', color: '#999' }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ── Main MeetingMode component ───────────────────────────────────────────────
export default function MeetingMode({ attendees = [], meetingTitle = '', onEnd, onDismiss }) {
  const [phase, setPhase] = useState('recording'); // 'recording' | 'summarising' | 'review' | 'error'
  const [transcript, setTranscript] = useState('');
  const [liveText, setLiveText] = useState('');
  const [actionItems, setActionItems] = useState([]); // real-time detected items
  const [postMeeting, setPostMeeting] = useState(null);
  const [attendeesPanelOpen, setAttendeesPanelOpen] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [elapsed, setElapsed] = useState(0); // seconds

  // Speaker label state
  const [speakerMode, setSpeakerMode] = useState(false);
  const speakerModeRef = useRef(false);
  const [segments, setSegments] = useState([]); // [{id, speaker, text}]
  const [pendingText, setPendingText] = useState('');
  const pendingTextRef = useRef('');

  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const transcriptRef = useRef('');
  const lastChunkPosRef = useRef(0);
  const chunkTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const transcriptEndRef = useRef(null);

  const normalisedAttendees = attendees.map(a =>
    typeof a === 'string' ? { name: a } : a
  );

  // All speaker chips: "Me" + each attendee
  const speakerChips = [
    { name: 'Me', colour: NAVY },
    ...normalisedAttendees.map((a, i) => ({ name: a.name, colour: getColour(i) })),
  ];

  function toggleSpeakerMode() {
    const next = !speakerMode;
    setSpeakerMode(next);
    speakerModeRef.current = next;
    // When turning off, flush any pending text back into the flat transcript
    if (!next && pendingTextRef.current.trim()) {
      pendingTextRef.current = '';
      setPendingText('');
    }
  }

  function labelSegment(speakerName) {
    const text = pendingTextRef.current.trim();
    if (!text) return;
    setSegments(prev => [...prev, { id: Date.now(), speaker: speakerName, text }]);
    pendingTextRef.current = '';
    setPendingText('');
  }

  const startRecording = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setErrorMsg('Live transcription is not supported in this browser. You can paste a transcript after the meeting.');
      return;
    }
    const r = new SR();
    r.lang = navigator.language || 'en-GB';
    r.continuous = true;
    r.interimResults = true;

    r.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          const text = event.results[i][0].transcript;
          transcriptRef.current += text + ' ';
          setTranscript(transcriptRef.current);
          // Also track pending text for speaker labelling
          if (speakerModeRef.current) {
            pendingTextRef.current += text + ' ';
            setPendingText(pendingTextRef.current);
          }
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setLiveText(interim);
    };

    r.onerror = () => { /* silently ignore — onend will restart */ };
    r.onend = () => {
      if (isRecordingRef.current) {
        try { r.start(); } catch (_) {}
      }
    };

    recognitionRef.current = r;
    isRecordingRef.current = true;
    try { r.start(); } catch (_) {
      setErrorMsg('Could not start microphone. Check browser permissions.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    isRecordingRef.current = false;
    try { recognitionRef.current?.stop(); } catch (_) {}
    setLiveText('');
  }, []);

  // Start on mount
  useEffect(() => {
    startRecording();
    elapsedTimerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    chunkTimerRef.current = setInterval(processChunk, 30000);
    return () => {
      stopRecording();
      clearInterval(elapsedTimerRef.current);
      clearInterval(chunkTimerRef.current);
    };
  }, []); // eslint-disable-line

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, liveText, segments, pendingText]);

  async function processChunk() {
    const full = transcriptRef.current;
    const chunk = full.slice(lastChunkPosRef.current).trim();
    if (!chunk || chunk.length < 40) return;
    lastChunkPosRef.current = full.length;
    try {
      const res = await api('/api/aidey/meeting-actions-chunk', {
        method: 'POST',
        body: JSON.stringify({ chunk, attendees: normalisedAttendees.map(a => a.name) }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data.items) && data.items.length > 0) {
        setActionItems(prev => {
          const next = [...prev];
          for (const item of data.items) {
            if (!next.find(x => x.task?.toLowerCase() === item.task?.toLowerCase())) {
              next.push(item);
            }
          }
          return next;
        });
      }
    } catch (_) {}
  }

  async function stopAndSummarise() {
    stopRecording();
    clearInterval(chunkTimerRef.current);
    clearInterval(elapsedTimerRef.current);
    setPhase('summarising');

    // If speaker mode was active, build a labelled transcript
    const labelledParts = segments.length > 0
      ? segments.map(s => `${s.speaker}: ${s.text}`).join('\n') +
        (pendingTextRef.current.trim() ? '\n' + pendingTextRef.current.trim() : '')
      : null;
    const full = (labelledParts || transcriptRef.current).trim();

    if (!full) {
      setErrorMsg('No transcript was captured. Check microphone access.');
      setPhase('error');
      return;
    }

    try {
      const res = await api('/api/aidey/meeting-summary', {
        method: 'POST',
        body: JSON.stringify({
          transcript: full,
          attendees: normalisedAttendees.map(a => a.name),
          meetingTitle: meetingTitle || 'Meeting',
        }),
      });
      if (!res.ok) throw new Error('Server error');
      const data = await res.json();
      setPostMeeting(data);
      setPhase('review');
    } catch (err) {
      setErrorMsg('Could not generate summary. The transcript has been saved.');
      setPhase('error');
    }
  }

  async function handleCreateTasks(approvedItems) {
    const results = [];
    for (const item of approvedItems) {
      try {
        const res = await api('/api/tasks', {
          method: 'POST',
          body: JSON.stringify({
            title: item.task,
            priority: 'Medium',
            taskType: 'Task',
            date: item.dueDate || new Date().toISOString().slice(0, 10),
            details: `Action item from meeting: ${meetingTitle || 'Meeting'}${item.assignee ? `. Assigned to: ${item.assignee}` : ''}`,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          results.push(data.task || data);
        }
      } catch (_) {}
    }
    onEnd({
      transcript: transcriptRef.current,
      postMeeting,
      tasksCreated: results.length,
      meetingTitle: meetingTitle || 'Meeting',
    });
  }

  function handleSendSummary() {
    if (!postMeeting) return;
    const emailBody = [
      `Hi,\n\nHere's a summary of our ${meetingTitle || 'meeting'} today.\n`,
      `SUMMARY\n${postMeeting.summary}`,
      postMeeting.decisions?.length ? `\nDECISIONS\n${postMeeting.decisions.map(d => `• ${d}`).join('\n')}` : '',
      postMeeting.actionItems?.length ? `\nACTION ITEMS\n${postMeeting.actionItems.map(a => `• ${a.task}${a.assignee ? ` — ${a.assignee}` : ''}${a.dueDate ? ` (by ${a.dueDate})` : ''}`).join('\n')}` : '',
      '\nBest regards',
    ].filter(Boolean).join('\n');

    onEnd({
      transcript: transcriptRef.current,
      postMeeting,
      tasksCreated: 0,
      meetingTitle: meetingTitle || 'Meeting',
      draftEmail: {
        to: normalisedAttendees.map(a => a.email || a.name).filter(Boolean).join(', '),
        subject: `Notes: ${meetingTitle || 'Meeting'}`,
        body: emailBody,
      },
    });
  }

  function handleDismiss() {
    stopRecording();
    clearInterval(chunkTimerRef.current);
    clearInterval(elapsedTimerRef.current);
    onDismiss();
  }

  function fmtElapsed(s) {
    const m = Math.floor(s / 60), sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 200,
      background: '#fff', display: 'flex', flexDirection: 'column',
      fontFamily: "'Raleway', sans-serif",
      overflow: 'hidden',
    }}>

      {/* Header */}
      <div style={{ background: NAVY, color: '#fff', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {phase === 'recording' && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e05252', animation: 'meetingBlink 1.2s infinite' }} />
            {fmtElapsed(elapsed)}
          </span>
        )}
        {phase === 'summarising' && <span style={{ fontSize: 12, fontWeight: 700 }}>Generating summary…</span>}
        {phase === 'review' && <span style={{ fontSize: 12, fontWeight: 700 }}>Meeting complete</span>}
        {phase === 'error' && <span style={{ fontSize: 12, fontWeight: 700, color: '#ffaaaa' }}>Error</span>}
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {meetingTitle || 'Meeting in progress'}
        </span>
        {phase === 'recording' && (
          <>
            {/* Speaker labels toggle */}
            <button
              onClick={toggleSpeakerMode}
              title={speakerMode ? 'Disable speaker labels' : 'Enable speaker labels'}
              style={{
                background: speakerMode ? '#E4E69B' : 'rgba(255,255,255,0.15)',
                color: speakerMode ? NAVY : '#fff',
                border: 'none', borderRadius: 6, padding: '5px 9px',
                fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              👤 Labels {speakerMode ? 'ON' : 'OFF'}
            </button>
            <button
              onClick={stopAndSummarise}
              style={{ background: '#e05252', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Stop & Summarise
            </button>
          </>
        )}
        {(phase === 'review' || phase === 'error') && (
          <button
            onClick={handleDismiss}
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}
          >
            ✕
          </button>
        )}
      </div>

      <style>{`
        @keyframes meetingBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
      `}</style>

      {phase === 'summarising' && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: '#999' }}>
          <div style={{ width: 32, height: 32, border: `3px solid ${ACCENT}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'meetingSpin 0.8s linear infinite' }} />
          <div style={{ fontSize: 13 }}>Analysing transcript with Claude…</div>
          <style>{`@keyframes meetingSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {phase === 'error' && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, padding: 20 }}>
          <div style={{ fontSize: 13, color: '#e05252', textAlign: 'center' }}>{errorMsg}</div>
          <button onClick={handleDismiss} style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 7, padding: '8px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Close</button>
        </div>
      )}

      {phase === 'review' && postMeeting && (
        <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
          <PostMeetingCard
            postMeeting={postMeeting}
            attendees={normalisedAttendees}
            onCreateTasks={handleCreateTasks}
            onSendSummary={handleSendSummary}
            onDismiss={handleDismiss}
          />
        </div>
      )}

      {phase === 'recording' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Attendees + speaker chips */}
          <div style={{ borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
            <button
              onClick={() => setAttendeesPanelOpen(p => !p)}
              style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#888', textAlign: 'left' }}
            >
              <span style={{ transform: attendeesPanelOpen ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.15s', display: 'inline-block' }}>›</span>
              Attendees ({normalisedAttendees.length})
            </button>
            {attendeesPanelOpen && (
              <div style={{ padding: '0 14px 8px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {normalisedAttendees.length === 0 && (
                  <span style={{ fontSize: 11, color: '#bbb' }}>No attendees added</span>
                )}
                {normalisedAttendees.map((a, i) => (
                  <span key={i} style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
                    background: getColour(i) + '20', color: getColour(i), border: `1px solid ${getColour(i)}40`,
                  }}>
                    {a.name}
                  </span>
                ))}
              </div>
            )}

            {/* Speaker label chips — only shown when speaker mode is on */}
            {speakerMode && (
              <div style={{ padding: '6px 14px 8px', borderTop: `1px solid ${BORDER}`, background: '#f8f8ff' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>
                  Tap to label last speech segment:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {speakerChips.map((chip, i) => (
                    <button
                      key={i}
                      onClick={() => labelSegment(chip.name)}
                      disabled={!pendingText.trim()}
                      title={pendingText.trim() ? `Label as ${chip.name}` : 'No unlabelled speech yet'}
                      style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                        background: pendingText.trim() ? chip.colour + '20' : '#f0f0f0',
                        color: pendingText.trim() ? chip.colour : '#bbb',
                        border: `1px solid ${pendingText.trim() ? chip.colour + '60' : '#ddd'}`,
                        cursor: pendingText.trim() ? 'pointer' : 'default',
                        transition: 'all 0.1s',
                      }}
                    >
                      {chip.name}
                    </button>
                  ))}
                </div>
                {pendingText.trim() && (
                  <div style={{ fontSize: 10, color: '#aaa', marginTop: 4, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    Pending: "{pendingText.trim().slice(0, 60)}{pendingText.trim().length > 60 ? '…' : ''}"
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Body: transcript + action items */}
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

            {/* Transcript */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {!transcript && !liveText && (
                <div style={{ color: '#bbb', fontSize: 12, marginTop: 8 }}>Listening… start speaking to capture notes.</div>
              )}
              {errorMsg && !transcript && (
                <div style={{ color: '#e05252', fontSize: 12 }}>{errorMsg}</div>
              )}

              {speakerMode ? (
                /* Speaker-labelled rendering */
                <>
                  {segments.map(seg => {
                    const chipIdx = speakerChips.findIndex(c => c.name === seg.speaker);
                    const colour = chipIdx >= 0 ? speakerChips[chipIdx].colour : NAVY;
                    return (
                      <p key={seg.id} style={{ margin: '0 0 4px', fontSize: 12, lineHeight: 1.6, color: '#333' }}>
                        <strong style={{ color: colour, marginRight: 4 }}>{seg.speaker}:</strong>
                        <span>{seg.text}</span>
                      </p>
                    );
                  })}
                  {pendingText.trim() && (
                    <p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: '#888', fontStyle: 'italic' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#bbb', marginRight: 4 }}>[unlabelled]</span>
                      {pendingText.trim()}
                    </p>
                  )}
                </>
              ) : (
                /* Flat rendering (default) */
                transcript.split(/\s{2,}|\n/).filter(Boolean).map((para, i) => (
                  <p key={i} style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: '#333' }}>
                    <AnnotatedText text={para} attendees={normalisedAttendees} />
                  </p>
                ))
              )}

              {liveText && (
                <p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: '#aaa', fontStyle: 'italic' }}>
                  {liveText}
                </p>
              )}
              <div ref={transcriptEndRef} />
            </div>

            {/* Real-time action items panel */}
            {actionItems.length > 0 && (
              <div style={{ width: 180, borderLeft: `1px solid ${BORDER}`, overflowY: 'auto', padding: '10px 10px', flexShrink: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Detected actions</div>
                {actionItems.map((item, i) => (
                  <div key={i} style={{ fontSize: 11, lineHeight: 1.4, marginBottom: 8, padding: '5px 7px', borderRadius: 6, background: BG, border: `1px solid ${BORDER}` }}>
                    <div style={{ fontWeight: 600, color: '#222', marginBottom: 2 }}>{item.task}</div>
                    {item.assignee && <div style={{ color: ACCENT, fontSize: 10 }}>{item.assignee}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
